import { useEffect, useMemo, type ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { METHODS, type ForecastElementCode, type ForecastView, type IngestionOutcome } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { compass8 } from '../data/guide.ts'
import { siteAltitude, useWater } from '../data/water.ts'
import { siteSettings } from '../data/waterUi.ts'
import {
  FORECAST_BLOCK_HOURS, HOURLY_CODES, aggText, ageText, chanceText, coversFuture, decimalsOf, forecastAge, forecastBlocks, forecastDayLabel, forecastDays, forecastHours, hourLabels,
  periodText, periodValues, quantityText, rangeText, unitOf, unitsConverted, useForecast, type ForecastAgg, type ForecastDay, type ForecastEt0, type ForecastSpan,
} from '../data/weatherForecast.ts'
import { fmtNum } from '../lib/format.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { BackLink, Banner, NotAllowed, Pill } from '../ui/components.tsx'
import { NoFutureLine } from '../ui/ForecastNoFuture.tsx'
import { ForecastSource } from '../ui/ForecastSource.tsx'
import { Picto } from '../ui/pictos.tsx'
import { scrollToHashWhenSettled } from '../ui/scrollToHash.ts'
import { useNow } from '../ui/useNow.ts'

/** M24-W, the Prévision page (docs/M24.md "Screens"; rules 1, 2, 6; ADR-125, ADR-126): the newest forecast of the site's connector for the Superintendent and the
 *  direction (forecast.read; never the shared workstation), read online through `api.forecast_view` and kept in the page's memory only. The next 48 hours in
 *  three-hour blocks (each block its hours (start, end]), an "heure par heure" fold of the same 48 hours AS DELIVERED (`source` line, "(unités converties)"),
 *  the ten days by club day with ET0 prévue (FAO-56 Penman-Monteith, its inputs and what is missing beside it), "Détails" (the models and their run times, the
 *  file's own point, the service's own evaporation and the 12-hour extremes with their periods), and the licence block `#licence` every source line links to.
 *  The rules of which hours belong where are data/weatherForecast.ts's. Nothing here decides: no colour on a value, no window, no comparison with what was measured. */
export function ForecastPage() {
  const { t } = useTranslation()
  const { can, kiosk } = useClub()
  const allowed = can('forecast.read') && !kiosk
  const read = useForecast(allowed)
  if (!allowed) return <NotAllowed permission="forecast.read" />
  const v = read.view
  const offline = !read.online || read.error === 'OFFLINE'
  const state = v ? 'ready' : v === null ? 'none' : offline ? 'offline' : read.error ? 'error' : 'loading'
  return (
    <section className="flex flex-col gap-5" data-testid="forecast-page" data-state={state}>
      <BackLink to="/">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="weather" className="size-7 text-brand dark:text-fairway" />{t('forecast.title')}</h1>
      {offline && <Banner tone="warn"><span data-testid="fc-page-offline">{t('forecast.needsNetwork')}</span></Banner>}
      <p className="meta">{t('forecast.page.help')}</p>
      {state === 'loading' && <p className="meta" data-testid="fc-page-loading">{t('forecast.loading')}</p>}
      {state === 'error' && <Banner tone="warn"><span data-testid="fc-page-error">{t('forecast.error')}</span></Banner>}
      {state === 'none' && <p className="card" data-testid="fc-page-none">{t('forecast.none')}</p>}
      {v && <ForecastBody view={v} receivedAt={read.receivedAt} />}
    </section>
  )
}

type T = (key: string, o?: Record<string, unknown>) => string
const OUTCOME_TEXT: Record<IngestionOutcome, (t: T) => string> = {
  ok: (t) => t('forecast.settings.outcome.ok'), duplicate: (t) => t('forecast.settings.outcome.duplicate'), not_modified: (t) => t('forecast.settings.outcome.not_modified'),
  unchanged: (t) => t('forecast.settings.outcome.unchanged'), refused: (t) => t('forecast.settings.outcome.refused'), failed: (t) => t('forecast.settings.outcome.failed'),
}
/** The words of a run's outcome (a code of `integ.ingestion_run.outcome`; an unknown one as it is). */
export const outcomeText = (outcome: string, t: T): string => (outcome in OUTCOME_TEXT ? OUTCOME_TEXT[outcome as IngestionOutcome](t) : outcome)
/** "à 10,7 km" or "distance inconnue". */
export const distanceText = (view: Pick<ForecastView, 'issue'>, lang: string, t: T): string =>
  (view.issue.distance_km === null ? t('forecast.source.distanceUnknown') : t('forecast.source.distance', { km: fmtNum(view.issue.distance_km, lang, 1) }))
const word = (w: { fr: string; en: string }, lang: string) => (lang.startsWith('en') ? w.en : w.fr)

/** ET0 prévue of a day as words: the value, or why there is none (an incomplete day, an element the point does not give, a parameter of the club missing). */
export function Et0Value({ view, et0, testId = 'fc-day-et0' }: { view: ForecastView; et0: ForecastEt0; testId?: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const label = (c: ForecastElementCode) => { const e = view.elements.find((x) => x.code === c); return e ? word(e.label, lang) : c }
  const param = (p: string) => (p === 'latitude' ? t('forecast.page.missingLatitude') : p === 'altitude' ? t('forecast.page.missingAltitude') : t('forecast.page.missingOther'))
  let text: string
  if (et0.status === 'ok') text = quantityText(et0.et0, 'mm', 1, lang)
  else if (et0.status === 'missing') text = `— ${et0.missing.length ? t('forecast.page.et0Missing', { list: et0.missing.map(param).join(', ') }) : t('forecast.page.et0Invalid', { list: et0.invalid.map(param).join(', ') })}`
  else {
    const notGiven = et0.lacking.filter((c) => !view.elements.find((e) => e.code === c)?.provided)
    text = `— ${notGiven.length ? t('forecast.page.et0NotGiven', { list: notGiven.map(label).join(', ') }) : t('forecast.page.et0Incomplete')}`
  }
  return <span data-testid={testId} data-status={et0.status}>{text}</span>
}
/** The inputs ET0 prévue was worked from, beside it (as for Hargreaves-Samani on the water page). */
export function Et0Inputs({ et0, testId = 'fc-day-et0-inputs' }: { et0: ForecastEt0; testId?: string }) {
  const { t, i18n } = useTranslation()
  if (et0.status === 'incomplete') return null
  const lang = i18n.language, x = et0.inputs, n = (v: number | null, d: number) => (v === null ? '—' : fmtNum(v, lang, d))
  // the club's two parameters with their unit, or a dash alone when the club has not given one ("altitude —", never "— m")
  const lat = x.latitudeDeg === null ? '—' : `${fmtNum(x.latitudeDeg, lang, 4)}°`, alt = x.altitudeM === null ? '—' : quantityText(x.altitudeM, 'm', 0, lang)
  return (
    <p className="meta" data-testid={testId}>
      {t('forecast.page.et0Inputs', {
        tmin: quantityText(x.tminC, 'degC', 1, lang), tmax: quantityText(x.tmaxC, 'degC', 1, lang), td: quantityText(x.meanDewPointC, 'degC', 1, lang), ea: n(x.eaKpa, 2),
        wind: quantityText(x.meanWindKmh, 'km_h', 1, lang), u2: n(x.u2Ms, 1), rs: n(x.rsMj, 2), lat, alt,
      })}
    </p>
  )
}

function ForecastBody({ view, receivedAt }: { view: ForecastView; receivedAt: number }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const water = useWater()
  const now = useNow(60_000)
  const lang = i18n.language, tz = site.timezone
  const hash = useRouterState({ select: (s) => s.location.hash })
  useEffect(() => (hash === 'licence' ? scrollToHashWhenSettled('licence') : undefined), [hash])
  const latitude = siteSettings(water.profiles).latitude, altitude = siteAltitude(water.profiles)
  // the minute is enough: the blocks and the days move on with it, the page is read, not watched
  const minute = Math.floor(now / 60_000)
  const blocks = useMemo(() => forecastBlocks(view, { now: minute * 60_000, tz }), [view, minute, tz])
  const hours = useMemo(() => forecastHours(view, { now: minute * 60_000 }), [view, minute])
  const days = useMemo(() => forecastDays(view, { now: minute * 60_000, tz, latitude, altitude }), [view, minute, tz, latitude, altitude])
  const age = forecastAge(view, now, receivedAt)
  const today = localDate(now, tz)
  const q = (a: ForecastAgg, code: ForecastElementCode) => aggText(a, unitOf(view, code), decimalsOf(view, code), lang, t)
  const dayLabel = (date: string) => forecastDayLabel(date, lang, tz)
  const future = coversFuture(view, minute * 60_000)
  const hourRows = hourLabels(hours.map((x) => x.at), tz)
  const time = (ms: number) => formatLocal(ms, tz, 'HH:mm')
  const temps = (s: ForecastSpan) => (s.tmin.kind === 'value' && s.tmax.kind === 'value' ? rangeText(s.tmin.value, s.tmax.value, unitOf(view, 'air_temperature'), decimalsOf(view, 'air_temperature'), lang, t) : q(s.tmin, 'air_temperature'))
  // a block's temperature: an instant window with no step of the issue (the last part of a block's third hour) says so, labelled — never a bare "pas de valeur"
  const blockTemp = (s: ForecastSpan) => (s.tmin.kind === 'none' && s.tmin.of === 0 ? t('forecast.page.noHourInBlock') : temps(s))
  const rainLine = (s: ForecastSpan) => (s.rain.kind === 'value' ? `${q(s.rain, 'rain_1h')} · ${chanceText(s.rainChance, lang, t)}` : q(s.rain, 'rain_1h'))
  const gust = (s: ForecastSpan) => (s.gust.kind === 'value' ? t('forecast.gusts', { gust: quantityText(s.gust.value, unitOf(view, 'wind_gust_1h'), decimalsOf(view, 'wind_gust_1h'), lang) }) : q(s.gust, 'wind_gust_1h'))
  const wind = (s: ForecastSpan) => (s.wind.kind === 'value' ? t('forecast.windMean', { speed: quantityText(s.wind.value, unitOf(view, 'wind_speed'), decimalsOf(view, 'wind_speed'), lang), force: s.force ?? '—' }) : q(s.wind, 'wind_speed'))
  const label = (c: ForecastElementCode) => { const e = view.elements.find((x) => x.code === c); return e ? word(e.label, lang) : c }
  const dist = distanceText(view, lang, t)
  const ground = (d: ForecastDay) => (d.groundMin.kind === 'value' ? `${quantityText(d.groundMin.value, unitOf(view, 'ground_temperature_min_12h'), decimalsOf(view, 'ground_temperature_min_12h'), lang)} ${periodText(d.groundMin.from, d.groundMin.to, tz, t)}` : d.groundMin.kind === 'notProvided' ? t('forecast.notProvided') : t('forecast.noValue'))

  return (
    <>
      <div className="card flex flex-col gap-1" data-testid="fc-header">
        <p className="text-lg font-bold" data-testid="fc-point">
          {t('forecast.page.point', { point: view.connector.point_label, code: view.issue.point_code, distance: dist })}
          {view.issue.bearing_deg !== null && ` · ${t('forecast.page.direction', { dir: t(`guide.dir.${compass8(view.issue.bearing_deg)}`) })}`}
        </p>
        <p className="flex flex-wrap items-center gap-2">
          <span data-testid="fc-issued">{t('forecast.issuedOn', { date: formatLocal(view.issue.issued_at, tz, 'dd/MM'), time: time(Date.parse(view.issue.issued_at)), age: ageText(age.ms, t) })}</span>
          {age.stale && <Pill tone="gray"><span data-testid="fc-stale">{t('forecast.stale')}</span></Pill>}
        </p>
        {/* « (à confirmer) » only while the club keeps the kind's default (rule 12; Réglages compares them the same way) */}
        <p className="meta" data-testid="fc-stale-rule">{t('forecast.staleRule', { count: age.staleAfterHours, confirm: view.connector.stale_after_hours === view.connector.default_stale_after_hours ? ` ${t('forecast.settings.toConfirm')}` : '' })}</p>
        <p className="meta" data-testid="fc-last-run">{view.last_run ? t('forecast.lastRun', { outcome: outcomeText(view.last_run.outcome, t), when: formatLocal(view.last_run.finished_at, tz) }) : t('forecast.noRun')}</p>
        {!view.connector.enabled && <p className="meta" data-testid="fc-off">{t('forecast.switchedOff')}</p>}
      </div>

      {!future && <div className="card"><NoFutureLine view={view} ageMs={age.ms} stale={age.stale} testId="fc-page-past" /></div>}

      {future && <>
      <section className="flex flex-col gap-3" data-testid="fc-blocks">
        <h2 className="text-xl font-bold">{t('forecast.page.blocks')}</h2>
        <p className="meta">{t('forecast.page.blocksHelp')}</p>
        <p className="meta" data-testid="fc-thunder-line">{t('forecast.thunderLine')}</p>
        <ul className="card flex flex-col divide-y divide-slate-200 !py-1 dark:divide-night-line">
          {blocks.map((b, i) => {
            const newDay = i === 0 || localDate(b.from, tz) !== localDate(blocks[i - 1]!.from, tz)
            return (
              <li key={b.from} className="flex flex-col gap-0.5 py-2" data-testid="fc-block" data-from={new Date(b.from).toISOString()} data-partial={b.partial ? '1' : '0'}>
                <span className="font-semibold">
                  {newDay ? `${dayLabel(localDate(b.from, tz))} · ` : ''}{time(b.from)}–{time(b.to)}{b.cutFrom > b.from ? ` (${t('forecast.page.fromNow')})` : b.hours !== FORECAST_BLOCK_HOURS ? ` (${t('forecast.page.blockHours', { n: b.hours })})` : ''}
                </span>
                <span data-testid="fc-block-rain">{t('forecast.rain')} · {rainLine(b)}</span>
                <span data-testid="fc-block-temp">{t('forecast.temperature')} · {blockTemp(b)}</span>
                <span data-testid="fc-block-gust">{b.gust.kind === 'value' ? gust(b) : `${t('forecast.gust')} · ${gust(b)}`}</span>
                <span className="text-sm" data-testid="fc-block-thunder">{t('forecast.thunder')} · {chanceText(b.thunderChance, lang, t)}</span>
              </li>
            )
          })}
        </ul>
        <ForecastSource view={view} mode="derived" />
      </section>

      <details className="card" data-testid="fc-hourly">
        <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('forecast.page.hourly')}</summary>
        <p className="meta">{t('forecast.page.hourlyHelp')}</p>
        <ul className="mt-2 flex flex-col divide-y divide-slate-200 dark:divide-night-line">
          {hours.map((hr, i) => (
            <li key={hr.at} className="flex flex-col gap-0.5 py-2" data-testid="fc-hour" data-at={new Date(hr.at).toISOString()}>
              {/* the autumn night holds an hour twice: each says its season (never two identical lines) */}
              <span className="font-semibold" data-testid="fc-hour-label">{hourRows[i]!.label}{hourRows[i]!.season ? ` (${t(hourRows[i]!.season === 'summer' ? 'forecast.page.summerTime' : 'forecast.page.winterTime')})` : ''}</span>
              <span className="text-sm">
                {hr.values.map((x) => `${label(x.code)} ${x.value === null ? (x.provided ? '—' : t('forecast.notProvided')) : quantityText(x.value, x.unit, x.decimals, lang)}`).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
        <ForecastSource view={view} mode="source" converted={unitsConverted(view, HOURLY_CODES)} />
      </details>

      <section className="flex flex-col gap-3" data-testid="fc-days">
        <h2 className="text-xl font-bold">{t('forecast.page.days')}</h2>
        <p className="meta">{t('forecast.page.daysHelp')}</p>
        <p className="meta">{t('forecast.thunderLine')}</p>
        <ul className="grid gap-3 sm:grid-cols-2">
          {days.map((d) => (
            <li key={d.date} className="card flex flex-col gap-1" data-testid="fc-day" data-date={d.date} data-partial={d.partial ? '1' : '0'}>
              <h3 className="font-bold first-letter:uppercase">{dayLabel(d.date)}</h3>
              {d.date === today ? <p className="meta">{t('forecast.page.today')}</p> : d.partial && <p className="meta">{t('forecast.page.partialDay', { n: d.hours, of: d.dayHours })}</p>}
              <DayRow label={t('forecast.rain')} testId="fc-day-rain">{rainLine(d)}</DayRow>
              <DayRow label={t('forecast.thunder')} testId="fc-day-thunder">{chanceText(d.thunderChance, lang, t)}</DayRow>
              <DayRow label={t('forecast.temperature')} testId="fc-day-temp">{temps(d)}</DayRow>
              <DayRow label={t('forecast.wind')} testId="fc-day-wind">{[wind(d), gust(d)].join(' · ')}</DayRow>
              <DayRow label={t('forecast.groundMin')} testId="fc-day-ground">{ground(d)}</DayRow>
              <DayRow label={t('forecast.page.et0')} testId="fc-day-et0-row"><Et0Value view={view} et0={d.et0} /></DayRow>
              <Et0Inputs et0={d.et0} />
            </li>
          ))}
        </ul>
        <ForecastSource view={view} mode="derived" />
        <p className="meta" data-testid="fc-et0-method">{t('forecast.page.et0Method', { name: METHODS.penman_monteith.name, version: METHODS.penman_monteith.version, source: METHODS.penman_monteith.source })}</p>
        <ForecastSource view={view} mode="et0" />
      </section>
      </>}

      <details className="card" data-testid="fc-details">
        <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('forecast.page.details')}</summary>
        <div className="mt-2 flex flex-col gap-3 text-sm">
          <div>
            <h3 className="font-bold">{t('forecast.page.models')}</h3>
            <ul data-testid="fc-models">{view.issue.models.map((m) => <li key={m.name}>{t('forecast.page.model', { name: m.name, time: formatLocal(m.reference_time, tz, 'dd/MM HH:mm') })}</li>)}</ul>
            <p className="meta">{t('forecast.page.product', { product: view.issue.product, process: view.issue.generating_process, issuer: view.issue.issuer })}</p>
          </div>
          <p data-testid="fc-file-point">{t('forecast.page.filePoint', { name: view.issue.point_name, code: view.issue.point_code, lat: fmtNum(view.issue.point_lat, lang, 5), lon: fmtNum(view.issue.point_lon, lang, 5), height: fmtNum(view.issue.point_height_m, lang, 1) })}</p>
          <PeriodList view={view} code="potential_evaporation_24h" now={now} testId="fc-evaporation" note={<p className="meta" data-testid="fc-evaporation-note">{t('forecast.page.serviceEvaporation')}</p>} />
          {(['air_temperature_min_12h', 'air_temperature_max_12h', 'ground_temperature_min_12h'] as const).map((c) => <PeriodList key={c} view={view} code={c} now={now} testId="fc-extreme" />)}
          <p className="meta">{t('forecast.page.periodValues')}</p>
          {/* these are the provider's values as delivered (converted from K): their source line too (rule 6) */}
          <ForecastSource view={view} mode="source" testId="fc-details-source" converted={unitsConverted(view, ['potential_evaporation_24h', 'air_temperature_min_12h', 'air_temperature_max_12h', 'ground_temperature_min_12h'])} />
        </div>
      </details>

      <section id="licence" className="card flex scroll-mt-20 flex-col gap-2" data-testid="fc-licence">
        <h2 className="text-xl font-bold">{t('forecast.page.licence')}</h2>
        <p data-testid="fc-licence-provider">{t('forecast.page.licenceProvider', { kind: word(view.source.kind_label, lang) })}</p>
        <p>{word(view.issue.attribution, lang)}</p>
        {view.issue.licence && <p>{t('forecast.page.licenceTerms', { licence: view.issue.licence })}</p>}
        {view.issue.licence_url && <a href={view.issue.licence_url} target="_blank" rel="noopener noreferrer" className="flex min-h-14 items-center break-all font-semibold text-brand underline underline-offset-2 dark:text-fairway" data-testid="fc-licence-link">{view.issue.licence_url}</a>}
        <p>{t('forecast.page.licencePoint', { point: view.connector.point_label, code: view.issue.point_code, distance: dist, name: view.issue.point_name })}</p>
        <p>{t('forecast.page.licenceComputes')}</p>
        <p className="meta">{t('forecast.page.licenceNoAdvice')}</p>
      </section>
    </>
  )
}

function DayRow({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return <p className="flex flex-col"><span className="text-sm font-semibold">{label}</span><span data-testid={testId}>{children}</span></p>
}

/** The values of a 12- or 24-hour element after now, each with its period (the element's own label: the service's own figure is named so). */
function PeriodList({ view, code, now, testId, note }: { view: ForecastView; code: ForecastElementCode; now: number; testId: string; note?: ReactNode }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const el = view.elements.find((e) => e.code === code)
  if (!el) return null
  const values = periodValues(view, code, { now })
  return (
    <div>
      <h3 className="font-bold">{word(el.label, lang)}</h3>
      {note}
      {!el.provided ? <p>{t('forecast.notProvided')}</p> : values.length === 0 ? <p className="meta">{t('forecast.page.none')}</p> : (
        <ul>{values.map((p) => <li key={p.at} data-testid={testId}>{quantityText(p.value, el.unit, el.decimals, lang)} {periodText(p.from, p.to, site.timezone, t, true)}</li>)}</ul>
      )}
    </div>
  )
}
